/**
 * Compatibility helpers shared across the auto-select planning and global
 * search modules.
 *
 *   - normalizeDCIsolator: resolves voltage_to_current_rating from the many
 *     shapes it can arrive in (top-level object, top-level JSON string, nested
 *     inside `data`, or only present locally in AccountHelper by code).
 *   - isEligibleForGlobalResults: excludes already-active MyList entries from
 *     global-search results to avoid duplicates.
 *   - filterByCompatibility: slot-aware filter using the same compatibility
 *     rules as electrical validation/auto-fill.
 *   - rankBySupplierPreference: stable sort by org-configured distributor order.
 */
import { OtherComponentType } from 'types/otherComponent'
import { SlotType } from 'types/slots'
import { parseJsonSafe } from 'util/misc'
import { V2Component } from './autoSelectTypes'
import { getConfiguredOrDefaultDCCableLengthMeters } from './dcCableLengthDefaults'
import { filterToDCCableReelSkus } from './dcCableReelLength'
import { getInverterDCCableSegments } from './electricalDCCable'
import {
  getACIsolatorCompatibility,
  getCableVoltageDrop,
  getDCIsolatorCompatibility,
  getDCStringVoltage,
  getMaxCableCurrent,
} from './helpers'

export function normalizeACIsolator(component: V2Component): V2Component {
  const dataParsed = parseJsonSafe(component.data, null) as Record<string, unknown> | null
  const currentRating = component.current_rating ?? dataParsed?.current_rating
  const voltageRating = component.voltage_rating ?? dataParsed?.voltage_rating
  const phaseType = component.phase_type ?? dataParsed?.phase_type

  if (
    currentRating === component.current_rating &&
    voltageRating === component.voltage_rating &&
    phaseType === component.phase_type
  ) {
    return component
  }

  return {
    ...component,
    current_rating: currentRating != null ? Number(currentRating) : component.current_rating,
    voltage_rating: voltageRating != null ? Number(voltageRating) : component.voltage_rating,
    phase_type: phaseType != null ? String(phaseType) : component.phase_type,
  }
}

export function passesACIsolatorCurrentPickFilter(component: V2Component, outputCurrentAmps: number): boolean {
  const rating = Number(component.current_rating)
  return Number.isFinite(rating) && Number.isFinite(outputCurrentAmps) && outputCurrentAmps < rating
}

export function normalizeDCIsolator(component: any) {
  let ratingMapRaw = component?.voltage_to_current_rating
  if (typeof ratingMapRaw === 'string') {
    const parsed = parseJsonSafe(ratingMapRaw, null)
    if (parsed) ratingMapRaw = parsed
  }
  if (!ratingMapRaw || typeof ratingMapRaw !== 'object') {
    const dataParsed = parseJsonSafe(component?.data, null)
    const fromData = dataParsed?.voltage_to_current_rating
    if (typeof fromData === 'string') {
      ratingMapRaw = parseJsonSafe(fromData, null) ?? fromData
    } else if (fromData) {
      ratingMapRaw = fromData
    }
  }
  if (!ratingMapRaw || typeof ratingMapRaw !== 'object') {
    const code = component?.code
    if (code) {
      const locals: any[] = window.AccountHelper?.getComponentOtherSpecsAvailable?.(true) || []
      const localMatch = locals.find((item: any) => item?.code === code)
      const localRating = localMatch?.voltage_to_current_rating
      if (typeof localRating === 'string') {
        ratingMapRaw = parseJsonSafe(localRating, null) ?? ratingMapRaw
      } else if (localRating && typeof localRating === 'object') {
        ratingMapRaw = localRating
      }
    }
  }
  if (ratingMapRaw && typeof ratingMapRaw === 'object') {
    return { ...component, voltage_to_current_rating: ratingMapRaw }
  }
  return component
}

/**
 * Global search results must exclude MyList + non-archived entries: those
 * would already have been picked up by the local pass, so including them
 * causes duplicates and misleading "add to MyList" flows. Archived MyList
 * items are eligible because we unarchive them on confirmation.
 */
export function isEligibleForGlobalResults(component: any): boolean {
  return !(component?.is_my_list === true && component?.is_archived !== true)
}

function passesCableVoltageDrop(
  cable: V2Component,
  cableLength: number,
  supplyPhase: 'single_phase' | 'three_phase',
  location: { type?: string; moduleQuantity?: () => number; modules?: unknown[] }
): boolean {
  const current = getMaxCableCurrent(location)
  const dcVoltage = getDCStringVoltage(location)
  if (!current || cableLength <= 0) return true

  const result = getCableVoltageDrop(
    cable as unknown as OtherComponentType,
    cableLength,
    supplyPhase,
    current,
    dcVoltage
  )
  return result.compatibility !== 'bad' && !result.data?.error
}

/** Reel-length guard + voltage-drop compatibility for inverter-level auto-select. */
export function refineInverterDCCableCatalog(
  components: V2Component[],
  slot: SlotType,
  reelLengthOptions: number[]
): V2Component[] {
  return filterByCompatibility(filterToDCCableReelSkus(components, reelLengthOptions), slot)
}

export function filterByCompatibility(candidates: V2Component[], slot: SlotType): V2Component[] {
  const attachObj = slot.attachToUuid ? window.editor.objectByUuid(slot.attachToUuid) : null
  if (!attachObj) return candidates

  if (slot.type === 'dc_isolator') {
    const strings = attachObj.type === 'OsString' ? [attachObj] : attachObj.children || []
    return candidates.filter((c) => {
      const normalized = normalizeDCIsolator(c)
      const result = getDCIsolatorCompatibility(normalized, strings)
      return result.compatibility !== 'bad' && !result.data?.error
    })
  }

  if (slot.type === 'ac_isolator') {
    return candidates.filter((c) => {
      const normalized = normalizeACIsolator(c)
      const result = getACIsolatorCompatibility(normalized as unknown as OtherComponentType, attachObj)
      return result.compatibility !== 'bad' && !result.data?.error
    })
  }

  if (slot.type === 'dc_cable') {
    const supplyPhase = window.WorkspaceHelper.project?.number_of_phases === 3 ? 'three_phase' : 'single_phase'

    if (attachObj.type === 'OsInverter' || attachObj.type === 'OsMicroinverter') {
      const system = attachObj.getSystem?.()
      if (!system) return candidates
      const segments = getInverterDCCableSegments(system, attachObj)
      if (!segments.length) return candidates

      return candidates.filter((c) =>
        segments.every((segment) => passesCableVoltageDrop(c, segment.length, supplyPhase, segment.location))
      )
    }

    const cableSlots = (attachObj.getSystem?.()?.userData?.slots || []) as SlotType[]
    const segmentSlot = cableSlots.find((s) => s.type === 'dc_cable' && s.attachToUuid === slot.attachToUuid)
    const cableLength = getConfiguredOrDefaultDCCableLengthMeters(segmentSlot?.properties?.cableLength, attachObj)
    if (cableLength <= 0) return candidates

    return candidates.filter((c) => passesCableVoltageDrop(c, cableLength, supplyPhase, attachObj))
  }

  return candidates
}

export function rankBySupplierPreference(components: V2Component[], orderedSuppliers: string[]): V2Component[] {
  if (!orderedSuppliers.length || !components.length) return components
  return [...components].sort((a, b) => {
    const aSupplierIdx = orderedSuppliers.findIndex((s) => a.ordering_v2?.some((o) => o.distributor === s))
    const bSupplierIdx = orderedSuppliers.findIndex((s) => b.ordering_v2?.some((o) => o.distributor === s))
    const aRank = aSupplierIdx === -1 ? orderedSuppliers.length : aSupplierIdx
    const bRank = bSupplierIdx === -1 ? orderedSuppliers.length : bSupplierIdx
    return aRank - bRank
  })
}

export function pickBestBySupplierPreference(
  components: V2Component[],
  orderedSuppliers: string[]
): V2Component | null {
  const ranked = rankBySupplierPreference(components, orderedSuppliers)
  return ranked[0] ?? null
}
